import { Injectable, OnDestroy, computed, signal } from '@angular/core';

interface StoredSession { token: string; expiresAt: string; profileComplete: boolean; }

const KEY_PREFIX = 'tc.cust.';

/**
 * D4A — customer session held in memory (signals) and mirrored to
 * sessionStorage, scoped per public token (`tc.cust.{publicToken}`).
 *
 * Security:
 *   • sessionStorage only (per-tab) — never localStorage, never the URL.
 *   • The token is never logged.
 *   • Auto-clears when expired (read path checks expiry).
 */
@Injectable({ providedIn: 'root' })
export class CustomerSessionService implements OnDestroy {
  private readonly _token = signal<string | null>(null);
  private readonly _expiresAt = signal<string | null>(null);
  private readonly _profileComplete = signal<boolean>(false);
  private boundToken: string | null = null;

  // TC-A1 — cross-tab sync: bound handler kept so it can be removed on destroy
  // (root singleton lives for the app's lifetime in prod; this keeps Jest's
  // per-test instances from accumulating listeners on the shared jsdom window).
  private readonly storageHandler = (e: StorageEvent) => this.onStorage(e);

  readonly token = this._token.asReadonly();
  readonly expiresAt = this._expiresAt.asReadonly();
  readonly profileComplete = this._profileComplete.asReadonly();
  readonly isAuthenticated = computed(() => {
    const t = this._token();
    const exp = this._expiresAt();
    if (!t || !exp) return false;
    return Date.parse(exp) > Date.now();
  });

  constructor() {
    // TC-A1 — keep sibling tabs of the same token in sync (sign-in/out elsewhere
    // flips this tab's signals → the global header chip updates everywhere).
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', this.storageHandler);
    }
  }

  ngOnDestroy(): void {
    if (typeof window !== 'undefined') {
      window.removeEventListener('storage', this.storageHandler);
    }
  }

  /**
   * TC-A1 — react ONLY to this tab's currently-bound token key. Mutates the
   * in-memory signals only; never writes back to storage and never touches
   * boundToken. The 'storage' event doesn't fire in the originating tab, so
   * there is no echo/loop.
   */
  private onStorage(e: StorageEvent): void {
    // Another tab called sessionStorage.clear() → key is null; treat as logout.
    if (e.key === null) { this.resetSignals(); return; }
    const bound = this.boundToken;
    if (!bound || e.key !== this.key(bound)) return;      // unrelated / other-token key

    if (e.newValue === null) { this.resetSignals(); return; } // logged out elsewhere

    let parsed: StoredSession | null = null;
    try {
      const p = JSON.parse(e.newValue) as StoredSession | null;
      if (p && typeof p.token === 'string' && typeof p.expiresAt === 'string') parsed = p;
    } catch { parsed = null; }
    if (!parsed) return;
    if (Date.parse(parsed.expiresAt) <= Date.now()) { this.resetSignals(); return; } // expired push
    this._token.set(parsed.token);
    this._expiresAt.set(parsed.expiresAt);
    this._profileComplete.set(parsed.profileComplete);
  }

  /** Bind to a public token and restore a non-expired stored session. */
  bindToken(publicToken: string): void {
    if (this.boundToken === publicToken) return;
    this.boundToken = publicToken;
    this.hydrate(publicToken);
  }

  /** Persist a freshly issued session (after OTP verify). */
  setSession(publicToken: string, token: string, expiresAt: string, profileComplete: boolean): void {
    this.boundToken = publicToken;
    this._token.set(token);
    this._expiresAt.set(expiresAt);
    this._profileComplete.set(profileComplete);
    this.write(publicToken, { token, expiresAt, profileComplete });
  }

  setProfileComplete(publicToken: string, complete: boolean): void {
    this._profileComplete.set(complete);
    const t = this._token(); const e = this._expiresAt();
    if (t && e) this.write(publicToken, { token: t, expiresAt: e, profileComplete: complete });
  }

  /** Explicit logout / clear. */
  clear(publicToken: string): void {
    this._token.set(null);
    this._expiresAt.set(null);
    this._profileComplete.set(false);
    try { sessionStorage.removeItem(this.key(publicToken)); } catch { /* ignore */ }
  }

  /** Current bearer value for the X-Customer-Token header, or null if expired. */
  currentToken(): string | null {
    return this.isAuthenticated() ? this._token() : null;
  }

  private hydrate(publicToken: string): void {
    const stored = this.read(publicToken);
    if (!stored) { this.resetSignals(); return; }
    if (Date.parse(stored.expiresAt) <= Date.now()) { this.clear(publicToken); return; }
    this._token.set(stored.token);
    this._expiresAt.set(stored.expiresAt);
    this._profileComplete.set(stored.profileComplete);
  }

  private resetSignals(): void {
    this._token.set(null);
    this._expiresAt.set(null);
    this._profileComplete.set(false);
  }

  private key(token: string): string { return `${KEY_PREFIX}${token}`; }
  private read(token: string): StoredSession | null {
    try {
      const raw = sessionStorage.getItem(this.key(token));
      if (!raw) return null;
      const parsed = JSON.parse(raw) as StoredSession | null;
      if (!parsed || typeof parsed.token !== 'string' || typeof parsed.expiresAt !== 'string') return null;
      return parsed;
    } catch { return null; }
  }
  private write(token: string, s: StoredSession): void {
    try { sessionStorage.setItem(this.key(token), JSON.stringify(s)); } catch { /* ignore */ }
  }
}
